import { getSupabase } from '$db/remote/supabase';
import { resizeToAvatar, buildAvatarPath } from './resize';
import type { AvatarUploadResult } from './types';

const BUCKET = 'avatars';

/**
 * Sube (o reemplaza) el avatar del usuario. Redimensiona antes de subir.
 * Devuelve la URL pública con cache-buster.
 */
export async function uploadAvatar(
  userId: string,
  file: File
): Promise<AvatarUploadResult> {
  const client = getSupabase();
  if (!client) return { ok: false, url: null, reason: 'no_supabase' };

  let blob: Blob;
  try {
    blob = await resizeToAvatar(file);
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'resize_failed';
    const reason =
      msg === 'too_large' || msg === 'invalid_type' ? msg : 'resize_failed';
    return { ok: false, url: null, reason };
  }

  const path = buildAvatarPath(userId);
  const { error } = await client.storage
    .from(BUCKET)
    .upload(path, blob, {
      contentType: 'image/webp',
      upsert: true,
      cacheControl: '3600'
    });

  if (error) {
    if (import.meta.env.DEV) {
      console.warn('[StudyQuantifier] uploadAvatar failed:', error.message);
    }
    return { ok: false, url: null, reason: 'upload_failed' };
  }

  return { ok: true, url: getAvatarPublicUrl(userId) };
}

export async function deleteAvatar(userId: string): Promise<boolean> {
  const client = getSupabase();
  if (!client) return false;
  const path = buildAvatarPath(userId);
  const { error } = await client.storage.from(BUCKET).remove([path]);
  if (error) {
    if (import.meta.env.DEV) {
      console.warn('[StudyQuantifier] deleteAvatar failed:', error.message);
    }
    return false;
  }
  return true;
}

/**
 * Construye la URL pública con un cache-buster opcional.
 * Añade `?v={timestamp}` para forzar recarga del navegador tras un upload.
 */
export function getAvatarPublicUrl(
  userId: string,
  cacheBuster?: number
): string {
  const client = getSupabase();
  if (!client) return '';
  const path = buildAvatarPath(userId);
  const { data } = client.storage.from(BUCKET).getPublicUrl(path);
  if (!data?.publicUrl) return '';
  if (cacheBuster === undefined) return data.publicUrl;
  return `${data.publicUrl}?v=${cacheBuster}`;
}

/**
 * Comprueba si existe un avatar para el usuario (HEAD request al bucket público).
 * Se usa al montar Preferencias para no mostrar preview rota si nunca subió uno.
 */
export async function checkAvatarExists(userId: string): Promise<boolean> {
  const client = getSupabase();
  if (!client) return false;
  try {
    // Usamos .list() en lugar de un HEAD fetch para evitar errores 400 en consola
    // cuando el archivo no existe. .list() devuelve un array vacío sin lanzar error.
    const { data, error } = await client.storage
      .from(BUCKET)
      .list(userId, { search: 'avatar.webp' });
    if (error) return false;
    return Array.isArray(data) && data.length > 0;
  } catch {
    return false;
  }
}